import { IconChevronRight, IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useState } from 'react';

import { Button, Card, Grid, Group, SegmentedControl, Tabs, Text, TextInput } from '@mantine/core';
import Editor from '@monaco-editor/react';
import { useLocation, useNavigate, useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import CrossDomainCopy from '../CrossDomainCopy';
import EventSelector from '../EventSelector';
import Mapper from '../KeyValueMapper';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import DomainSecretSelect from '../Settings/DomainSecretSelect';

const PipelineForm = ({ savePipeline, deletePipeline, ...rest }) => {
  const location = useLocation();
  const fetch = useFetch();
  const navigate = useNavigate();
  const search = queryString.parse(location.search);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pipeline, setPipeline] = useState(rest.pipeline);

  const [error, setError] = useState();
  const [eventTags, setEventTags] = useState();
  const [eventActions, setEventActions] = useState();

  const [loading, setLoading] = useState(false);
  const { accountId, domainId } = useParams();
  const tabs = ['general', 'filter', 'transform', 'output'];
  const tab = search.tab || tabs[0];

  const tabDisabled = {
    general: !pipeline.name || !pipeline.type || !pipeline.eventsAction,
    filter: !pipeline.filter,
    transform: !pipeline.transform,
    output: !pipeline.outputType || !pipeline.outputUrl,
  };

  const setTab = (tab) => {
    navigate(`${location.pathname}?${queryString.stringify({ ...search, tab })}`);
  };

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventTags`)
      .then(({ eventTags }) => setEventTags(eventTags))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventActions`)
      .then(({ eventActions }) => {
        setEventActions(eventActions);
      })
      .catch(setError);
  }, [accountId, domainId, fetch]);

  if (error) {
    return `An error has occurred: ${error.message}`;
  }

  if (pipeline === undefined || eventTags === undefined || eventActions === undefined) {
    return '';
  }
  const handleEditorDidMount = (editor, monaco) => {
    // Wait for editor to be fully ready
    setTimeout(() => {
      try {
        monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
          allowJs: true,
          checkJs: true,
          noUnusedLocals: true,
          noUnusedParameters: true,
          target: monaco.languages.typescript.ScriptTarget.ES2020,
          allowNonTsExtensions: true,
        });

        monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
          noSemanticValidation: false,
          noSyntaxValidation: false,
          diagnosticCodesToIgnore: [
            // Remove error codes you want to see
          ],
        });

        // Add your pipeline-specific globals
        //   monaco.languages.typescript.javascriptDefaults.addExtraLib(
        //     `
        //   declare var event: any;
        //   declare var profile: any;
        //   declare var data: any;
        //   declare var console: any;
        //   declare var window: any;
        // `,
        //     'pipeline-globals.d.ts'
        //   );
      } catch (error) {
        console.warn('Monaco config error (continuing):', error);
      }
    }, 500);
  };

  let placeholder = '';
  if (pipeline.outputType === 's3') {
    placeholder = 's3://bucket/prefix';
  } else if (pipeline.outputType === 'gcs') {
    placeholder = 'gs://bucket/prefix';
  } else if (pipeline.outputType === 'webhook') {
    placeholder = 'https://my-webhook.com/data';
  }

  let deleteButton;
  if (deletePipeline) {
    deleteButton = (
      <LockableButton restrictionKey={`pipelinesDelete`} color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  return (
    <form>
      <Tabs value={tab} onChange={setTab} mb="md" keepMounted>
        <Tabs.List>
          <Tabs.Tab value="general">General</Tabs.Tab>
          <Tabs.Tab value="filter">Advanced filter</Tabs.Tab>
          <Tabs.Tab value="transform">Transform</Tabs.Tab>
          <Tabs.Tab value="output">Output</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="general" pt={0}>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <Grid>
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Text size="sm" fw={500}>
                    Name*
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 9 }}>
                  <TextInput
                    name="name"
                    type="text"
                    value={pipeline.name}
                    placeholder="e.g. Idle Customers"
                    onChange={(e) => setPipeline({ ...pipeline, [e.target.name]: e.target.value })}
                  />
                </Grid.Col>
              </Grid>
              <Grid data-tour="blockDisplayModeSelect">
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Text size="sm" fw={500}>
                    Type
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 9 }}>
                  <SegmentedControl
                    disabled={!!pipeline.id}
                    value={pipeline.type ?? ''}
                    onChange={(type) => setPipeline({ ...pipeline, type })}
                    data={[
                      { label: 'Event', value: 'event' },
                      { label: 'Profile', value: 'profile' },
                    ]}
                  />
                  <Text size="sm" c="dimmed">
                    <strong>Profile</strong> - profile create / update events will be sent to your output <br />
                    <strong>Event</strong> - events will be sent to your output
                    <br />
                  </Text>
                </Grid.Col>
              </Grid>
              {pipeline.type === 'event' && (
                <>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 3 }}>
                      <Text size="sm" fw={500}>
                        Events action*
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 9 }}>
                      <SegmentedControl
                        value={pipeline.eventsAction ?? ''}
                        onChange={(eventsAction) => setPipeline({ ...pipeline, eventsAction })}
                        data={[
                          { label: 'Include', value: 'include' },
                          { label: 'Exclude', value: 'exclude' },
                        ]}
                      />
                    </Grid.Col>
                  </Grid>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 3 }}>
                      <Text size="sm" fw={500}>
                        Events*
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 9 }}>
                      {/* High-frequency events are kept out — a pipeline forwards every match to
                          its output — but not `allowedEventsForTrigger`, which additionally narrows
                          the list to events the domain has already observed. A pipeline is not a
                          trigger, and there is no reason to wait for an event to fire before you can
                          forward it: on a new domain that allowlist is empty. */}
                      <EventSelector
                        isMulti={true}
                        actions={pipeline.events}
                        setActions={(events) => setPipeline({ ...pipeline, events })}
                        excludeHighFrequency={true}
                      />
                    </Grid.Col>
                  </Grid>
                </>
              )}
            </Card.Section>
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="filter" pt={0}>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <Grid>
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Text size="sm" fw={500}>
                    Filter function*
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 9 }}>
                  <Editor
                    onMount={handleEditorDidMount}
                    defaultLanguage="javascript"
                    theme="xcode"
                    style={{ width: '100%' }}
                    onChange={(filter) => setPipeline({ ...pipeline, filter })}
                    name="filter"
                    height="380pt"
                    value={pipeline.filter}
                  />
                </Grid.Col>
              </Grid>
            </Card.Section>
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="transform" pt={0}>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <Grid>
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Text size="sm" fw={500}>
                    Transform function*
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 9 }}>
                  <Editor
                    defaultLanguage="javascript"
                    onMount={handleEditorDidMount}
                    style={{ width: '100%' }}
                    onChange={(transform) => setPipeline({ ...pipeline, transform })}
                    name="transform"
                    height="380pt"
                    value={pipeline.transform}
                  />
                </Grid.Col>
              </Grid>
            </Card.Section>
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="output" pt={0}>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <Grid>
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Text size="sm" fw={500}>
                    Output type*
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 9 }}>
                  <SegmentedControl
                    value={pipeline.outputType ?? ''}
                    onChange={(outputType) =>
                      setPipeline({
                        ...pipeline,
                        outputType,
                        secretId: outputType === 'webhook' ? pipeline.secretId : null,
                      })
                    }
                    data={[
                      { label: 'Amazon S3 Bucket', value: 's3' },
                      { label: 'Google Cloud Storage Bucket', value: 'gcs' },
                      { label: 'Webhook', value: 'webhook' },
                    ]}
                  />
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Text size="sm" fw={500}>
                    Output URL*
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 9 }}>
                  <TextInput
                    name="outputUrl"
                    type="text"
                    value={pipeline.outputUrl}
                    placeholder={placeholder}
                    onChange={(e) => setPipeline({ ...pipeline, [e.target.name]: e.target.value })}
                  />
                </Grid.Col>
              </Grid>
              {pipeline.outputType === 's3' && (
                <>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 3 }}>
                      <Text size="sm" fw={500}>
                        Output Region
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 9 }}>
                      <TextInput
                        name="outputRegion"
                        type="text"
                        value={pipeline.outputRegion}
                        placeholder="eu-west-1"
                        onChange={(e) => setPipeline({ ...pipeline, [e.target.name]: e.target.value })}
                      />
                    </Grid.Col>
                  </Grid>
                </>
              )}

              {pipeline.outputType === 'webhook' && (
                <>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 3 }}>
                      <Text size="sm" fw={500}>
                        Headers
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 9 }}>
                      <Mapper
                        rows={pipeline.headers}
                        setRows={(headers) => setPipeline({ ...pipeline, headers })}
                      />{' '}
                    </Grid.Col>
                  </Grid>
                  <Grid mt="md">
                    <Grid.Col span={{ base: 12, sm: 3 }}>
                      <Text size="sm" fw={500}>
                        Auth Secret
                      </Text>
                      <Text size="xs" c="dimmed">
                        Optional. Select a domain secret to use as the Authorization header value. Token-refresh secrets
                        are automatically refreshed before each call.
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 9 }}>
                      <DomainSecretSelect
                        accountId={accountId}
                        domainId={domainId}
                        placeholder="No secret (use static headers above)"
                        value={pipeline.secretId}
                        onChange={(secretId) => setPipeline({ ...pipeline, secretId })}
                        onError={setError}
                      />
                    </Grid.Col>
                  </Grid>
                </>
              )}
            </Card.Section>
          </Card>
        </Tabs.Panel>
      </Tabs>
      <Group justify="flex-end" gap="xs" py="md">
        <Button
          variant="default"
          onClick={() => {
            window.scrollTo(0, 0);
            navigate(-1);
          }}
        >
          {' '}
          Back
        </Button>
        {deleteButton}
        {pipeline.id && (
          <CrossDomainCopy
            resourceType="Pipeline"
            resourceId={pipeline.id}
            basePath="pipelines"
            restrictionKey={`pipelinesCreate`}
            fallbackAction={() => {
              navigate(
                location.pathname.replace(`/pipelines/${pipeline.id}/details`, `/pipelines/new?from=${pipeline.id}`)
              );
            }}
          />
        )}
        {pipeline.id || tabs.indexOf(tab) === tabs.length - 1 ? (
          <LockableButton
            data-tour="PipelinesSaveBtn"
            restrictionKey={`pipelines${pipeline.id ? 'Update' : 'Create'}`}
            onClick={() => {
              setLoading(true);
              savePipeline(pipeline).finally(() => setLoading(false));
            }}
            disabled={Object.keys(tabDisabled).some((k) => tabDisabled[k] === true) || loading}
          >
            <IconDeviceFloppy size={14} /> {loading ? 'Saving...' : 'Save'}
          </LockableButton>
        ) : (
          <Button
            onClick={() => {
              setTab(tabs[tabs.indexOf(tab) + 1]);
              window.scrollTo(0, 0);
            }}
            disabled={tabDisabled[tab]}
            data-tour="nextButtonPipelines"
          >
            Next <IconChevronRight size={14} />
          </Button>
        )}
        <ConfirmationModal
          show={confirmDeleteOpen}
          close={() => setConfirmDeleteOpen(false)}
          confirm={(cb) => deletePipeline(cb)}
          message="This action cannot be undone!"
        />
      </Group>
    </form>
  );
};

export default PipelineForm;
